import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Scale, Users, TrendingUp, GraduationCap, Building, Target, CheckCircle2, BarChart3, Clock, Award, MessageSquare, Lightbulb, Shield, Zap, Globe, Heart } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/motion';
import { services } from '@/data/index';
import { ServiceCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';

const businessServices = services.filter(service => 
  service.category === 'biznes' || service.category === 'szkolenia'
);

// ROI Data
const roiStats = [
  {
    metric: "Wzrost produktywności",
    value: "35%",
    description: "Średni wzrost wydajności zespołów po szkoleniach",
    icon: TrendingUp
  },
  {
    metric: "Redukcja konfliktów",
    value: "60%",
    description: "Zmniejszenie liczby konfliktów w organizacji",
    icon: Shield
  },
  {
    metric: "Retencja talentów",
    value: "45%",
    description: "Poprawa wskaźnika zatrzymania kluczowych pracowników",
    icon: Heart
  },
  {
    metric: "Czas rozwiązywania problemów",
    value: "-50%",
    description: "Skrócenie czasu potrzebnego na rozwiązanie sporów",
    icon: Clock
  }
];

// Process Steps
const processSteps = [
  {
    step: "01",
    title: "Analiza potrzeb",
    description: "Szczegółowa diagnoza organizacji i identyfikacja obszarów do rozwoju",
    icon: Target
  },
  {
    step: "02", 
    title: "Projektowanie rozwiązania",
    description: "Opracowanie dedykowanego programu dostosowanego do specyfiki firmy",
    icon: Lightbulb
  },
  {
    step: "03",
    title: "Implementacja",
    description: "Realizacja szkoleń, mediacji lub programów mentorskich",
    icon: Zap
  },
  {
    step: "04",
    title: "Monitoring i ewaluacja",
    description: "Śledzenie postępów i dostosowywanie działań do osiąganych rezultatów",
    icon: BarChart3
  }
];

// Business Benefits
const businessBenefits = [
  {
    title: "Lepsza komunikacja",
    description: "Skuteczniejsze przekazywanie informacji i budowanie relacji w zespole",
    icon: MessageSquare
  },
  {
    title: "Zwiększona innowacyjność",
    description: "Rozwój kreatywnego myślenia i otwartości na nowe rozwiązania",
    icon: Lightbulb
  },
  {
    title: "Silniejsze przywództwo",
    description: "Rozwój kompetencji menedżerskich i umiejętności zarządzania zespołem",
    icon: Users
  },
  {
    title: "Większa odporność na stres",
    description: "Budowanie resilience i radzenie sobie z wyzwaniami biznesowymi",
    icon: Shield
  },
  {
    title: "Poprawa kultury organizacyjnej",
    description: "Tworzenie pozytywnego środowiska pracy i wartości firmowych",
    icon: Building
  },
  {
    title: "Międzynarodowa współpraca",
    description: "Przygotowanie do pracy w środowisku międzykulturowym",
    icon: Globe
  }
];



export default function ServicesBusiness() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-serif text-foreground mb-6"
            >
              Rozwiązania <span className="text-accent">B2B</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-muted-foreground leading-relaxed mb-8"
            >
              Kompleksowe wsparcie dla organizacji w budowaniu silnych zespołów, 
              rozwiązywaniu konfliktów i rozwoju kompetencji przywódczych. 
              Sprawdzone metody oparte na psychologii biznesu.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Bezpłatna konsultacja <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>

            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ROI Stats Section */}
      <section className="py-16 lg:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Mierzalne korzyści dla Twojej organizacji
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Nasze programy przynoszą konkretne, wymierne rezultaty biznesowe
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {roiStats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center p-6 bg-background rounded-2xl shadow-sm border border-border hover:shadow-md transition-shadow"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="font-semibold text-foreground mb-2">{stat.metric}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Nasze usługi dla firm
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Kompleksowa oferta dostosowana do potrzeb nowoczesnych organizacji
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {businessServices.map(service => (
              <div key={service.id} className="flex flex-col">
                <ServiceCard service={service} />
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Jak pracujemy z firmami
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Sprawdzony proces gwarantujący maksymalne rezultaty
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative text-center"
              >
                <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {step.step}
                </div>
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4 -mt-12 relative z-10">
                  <step.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-border -translate-x-1/2" />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Korzyści dla Twojej organizacji
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Inwestycja w rozwój zespołu to inwestycja w przyszłość firmy
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {businessBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="p-6 bg-background rounded-2xl shadow-sm border border-border hover:shadow-md transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-serif text-foreground mb-6"
            >
              Gotowy na transformację swojej organizacji?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-muted-foreground leading-relaxed mb-8"
            >
              Skontaktuj się z nami, aby omówić potrzeby Twojej firmy i zaplanować 
              program dostosowany do Waszych celów biznesowych.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów bezpłatną konsultację <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                <a href="mailto:gabinetrachelanowak@gmail.com">
                  Wyślij zapytanie <MessageSquare className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}